/**
 * PATH: packages/email-hub/src/feature/lib/mailboxMenuItems.ts
 * FILE_ID: EMAILHUB-FEATURE-LIB-MAILBOX-MENU-ITEMS-001
 * PURPOSE: Pure builder for the right-click context-menu items shown on a
 *          connected mailbox row (reconnect, sync now, hide/show, settings).
 * WHY: The operator asked to right-click a connected mailbox and get
 *      actions (reconnect / settings / "more actions"). Building the item
 *      list as a pure I→O function keeps the panel lean and the action set
 *      unit-testable + trivially extensible.
 * FLOW:
 * ┌──────────────────────┐ ┌──────────────────┐ ┌────────────────────┐
 * │ acct + capability     │ → │ assemble actions │ → │ MailboxMenuItem[]  │
 * │ flags + callbacks     │   │ (skip if no cb)  │   │ (dividers + items) │
 * └──────────────────────┘ └──────────────────┘ └────────────────────┘
 * DEPENDENCIES:
 * - lucide-react: icon components rendered by MailboxContextMenu
 * - mailboxPresentation: shared provider label (single source of truth)
 * RELATED:
 * - MailboxContextMenu.tsx: renders these items
 * - ConnectedMailboxesPanel.tsx: produces the inputs + opens the menu
 */
import {
  Activity,
  Eye,
  EyeOff,
  Lock,
  Plug,
  RefreshCw,
  Settings,
  Sparkles,
  Users,
  type LucideIcon,
} from 'lucide-react'
import type { EmailAccount } from '../hooks/useEmails'
import { providerLabel } from './mailboxPresentation'

/** One actionable row, or a visual divider, in the mailbox context menu. */
export type MailboxMenuItem =
  | { kind: 'divider'; id: string }
  | {
      kind?: 'item'
      id: string
      label: string
      icon?: LucideIcon
      onClick: () => void
      disabled?: boolean
      danger?: boolean
    }

export interface BuildMailboxMenuItemsInput {
  acct: EmailAccount
  /** Whether a single-mailbox sync is possible (real EmailAccount id). */
  canSyncOne: boolean
  /** Whether the mailbox is currently hidden from the inbox. */
  isHidden: boolean
  /** Whether a stable visibility key exists for the hide/show toggle. */
  hasVisibilityKey: boolean
  /** Whether the mailbox is currently PRIVATE (account_type==='personal' —
   *  only the owner can see its emails). Drives the toggle label. */
  isPrivate?: boolean
  /** Whether the current user may change this mailbox's privacy. Only the
   *  owner of a real connected mailbox can; omit/false hides the toggle. */
  canManagePrivacy?: boolean
  onReconnect?: (acct: EmailAccount) => void
  onSync?: () => void
  onToggleHide?: () => void
  /** Flip the mailbox between private (personal) and shared. */
  onTogglePrivate?: () => void
  /** Open the preview-first Clean up inbox dialog for this mailbox. */
  onCleanUpInbox?: () => void
  onOpenSettings?: (tab: string) => void
}

/**
 * Pure I→O: capability flags + callbacks → ordered context-menu items.
 * Items whose callback is absent are omitted so the menu only ever offers
 * actions the host actually wired up.
 */
export function buildMailboxMenuItems(
  input: BuildMailboxMenuItemsInput,
): MailboxMenuItem[] {
  const { acct } = input
  const items: MailboxMenuItem[] = []

  if (input.onReconnect) {
    const reconnect = input.onReconnect
    items.push({
      id: 'reconnect',
      label: `Reconnect ${providerLabel(acct.provider)}`,
      icon: Plug,
      onClick: () => reconnect(acct),
    })
  }

  if (input.onSync) {
    const sync = input.onSync
    items.push({
      id: 'sync',
      label: 'Sync now',
      icon: RefreshCw,
      disabled: !input.canSyncOne,
      onClick: () => sync(),
    })
  }

  if (input.hasVisibilityKey && input.onToggleHide) {
    const toggle = input.onToggleHide
    items.push({
      id: 'visibility',
      label: input.isHidden ? 'Show in inbox' : 'Hide from inbox',
      icon: input.isHidden ? Eye : EyeOff,
      onClick: () => toggle(),
    })
  }

  // Privacy toggle — owner-only. "Mark as private" makes the mailbox
  // ``account_type='personal'`` so only the owner sees its emails;
  // "Make shared" reverses it. Hidden when the user can't manage privacy
  // (not the owner, or a synthetic legacy bucket with no real account id).
  if (input.canManagePrivacy && input.onTogglePrivate) {
    const togglePrivate = input.onTogglePrivate
    items.push({
      id: 'privacy',
      label: input.isPrivate ? 'Make shared' : 'Mark as private',
      icon: input.isPrivate ? Users : Lock,
      onClick: () => togglePrivate(),
    })
  }

  if (input.onCleanUpInbox && input.canSyncOne) {
    const cleanUp = input.onCleanUpInbox
    if (items.length > 0) items.push({ kind: 'divider', id: 'div-sweep' })
    items.push({
      id: 'clean-up-inbox',
      label: 'Clean up inbox…',
      icon: Sparkles,
      onClick: () => cleanUp(),
    })
  }

  if (input.onOpenSettings) {
    const open = input.onOpenSettings
    if (items.length > 0) items.push({ kind: 'divider', id: 'div-settings' })
    items.push({
      id: 'sync-status',
      label: 'Sync & status',
      icon: Activity,
      onClick: () => open('email-sync'),
    })
    items.push({
      id: 'settings',
      label: 'Email settings',
      icon: Settings,
      onClick: () => open('email'),
    })
  }

  return items
}
